/**
 * LandingThemePage — le composant UNIQUE des 16 landings thématiques.
 *
 * Une landing = une entrée de `content/landings.js` + une route. Rien
 * d'autre : la structure, les styles et les parcours vivent ici, une
 * seule fois. Ajouter un 17e thème n'ajoute pas une ligne de JSX.
 *
 * Structure, reprise des maquettes validées :
 *   header public · hero (badge, titre, sous-titre, dropzone, micro-ligne)
 *   · « Ce qu'Archer vérifie » (6 cartes) · bloc IA + mascotte
 *   · LA PREUVE (carte d'analyse exemple) · 3 cartes d'action
 *   · bande juriste-avocat · FAQ · CTA final · footer
 *
 * Comme HomeV2/PlansV2/TeamsPage, les valeurs structurantes restent en
 * style inline (ce sont celles des maquettes, elles se lisent en face du
 * markup) et landing-theme.css ne porte que hovers, focus, keyframes et
 * points de rupture.
 *
 * Écarts avec les maquettes, tous voulus :
 *  - Header et footer : ceux du site (PublicHeader, SiteFooterV2). Les
 *    maquettes embarquent un chrome anglais de gabarit dont le footer
 *    porte les mentions bannies (garantie 30 jours, « nos avocats »,
 *    conformité RGPD). Le prompt impose de toute façon le header public
 *    standard ; le footer suit la même logique.
 *  - La dropzone est RÉELLE. Les maquettes simulaient une analyse avec
 *    une barre de progression ; ici le fichier part dans le vrai
 *    parcours (inscription puis /upload, cf. utils/pendingUpload.js).
 *  - La mascotte vient de assets/, pas d'un base64 embarqué, à la même
 *    taille (170×150) et à la même place.
 */
import React, { useCallback, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import PublicHeader from '../components/PublicHeader';
import SiteFooterV2 from '../components/siteV2/SiteFooterV2';
import PageHead from '../components/seo/PageHead';
import JsonLd, { createFaqSchema, createBreadcrumbSchema } from '../components/seo/JsonLd';
import GoogleAnalytics from '../components/seo/GoogleAnalytics';
import { getPageMetadata, SITE_URL } from '../lib/seo/metadata';
import { useAuth } from '../contexts/AuthContext';
import { setPendingUpload, validatePendingFile } from '../utils/pendingUpload';
import { ROUTE_PLANS, ROUTE_ATTORNEYS, ROUTE_START } from '../components/siteV2/routes';
import useStartRoute from '../hooks/useStartRoute';
import { landingTheme } from '../content/landingTheme';
import { useSiteLanguage } from '../hooks/useSiteLanguage';
// Le compte du corpus est LU, pas retapé. `landingCommon.json` annonçait
// « 2.5M documents » sur les seize landings, dans les trois langues —
// environ 600 fois la taille réelle de `legal_db_v2`. Le correctif existait
// déjà pour « Notre IA » (`components/engine/corpus`) ; il n'avait
// simplement jamais été propagé ici.
import { corpusCountPlus } from '../components/engine/corpus';
import { useLandingT } from '../hooks/useLandingT';
import MascotPicture from '../components/common/MascotPicture';
import './landing-theme.css';

const BLUE = '#1a56db';
const INK = '#0a0a0f';
const INK_2 = '#4b5563';

/* ── Textes communs aux 16 thèmes ──────────────────────────────────
   Ils étaient identiques dans les 16 maquettes et vivaient donc ici,
   figés en français, plutôt que seize fois dans la config. Ils vivent
   maintenant dans i18n/landingCommon.json — même principe, une seule
   fois, mais dans les trois langues. Ne restent ici que les icônes des
   quatre statistiques du bloc IA : elles ne se traduisent pas. */
const AI_STAT_ICONS = ['doc', 'book', 'scale', 'sync'];
const AI_STAT_GREEN = [false, false, false, true];

/* ── Icônes ────────────────────────────────────────────────────────── */
const S = { fill: 'none', stroke: 'currentColor', strokeLinecap: 'round', strokeLinejoin: 'round' };
const IconUpload = () => (
  <svg width="26" height="26" viewBox="0 0 24 24" strokeWidth="1.8" stroke={BLUE} fill="none"
       strokeLinecap="round" strokeLinejoin="round" style={{ marginBottom: 10 }} aria-hidden="true">
    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
    <polyline points="17 8 12 3 7 8" /><line x1="12" x2="12" y1="3" y2="15" />
  </svg>
);
const IconCheck = () => (
  <svg width="11" height="11" viewBox="0 0 24 24" strokeWidth="3.5" stroke="#16a34a" fill="none"
       strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><polyline points="20 6 9 17 4 12" /></svg>
);
const IconScale = ({ size = 14, color = BLUE, style }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" strokeWidth="2" stroke={color} style={style} {...S} aria-hidden="true">
    <path d="m16 16 3-8 3 8c-.87.65-1.92 1-3 1s-2.13-.35-3-1Z" />
    <path d="m2 16 3-8 3 8c-.87.65-1.92 1-3 1s-2.13-.35-3-1Z" />
    <path d="M7 21h10" /><path d="M12 3v18" /><path d="M3 7h2c2 0 5-1 7-2 2 1 5 2 7 2h2" />
  </svg>
);
const IconDoc = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" strokeWidth="2" stroke={BLUE} style={{ flex: 'none' }} {...S} aria-hidden="true">
    <path d="M14.5 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7.5L14.5 2z" /><polyline points="14 2 14 8 20 8" />
  </svg>
);
const IconBook = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" strokeWidth="2" stroke={BLUE} style={{ flex: 'none' }} {...S} aria-hidden="true">
    <path d="M2 3h6a4 4 0 0 1 4 4v14a3 3 0 0 0-3-3H2z" /><path d="M22 3h-6a4 4 0 0 0-4 4v14a3 3 0 0 1 3-3h7z" />
  </svg>
);
const IconSync = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" strokeWidth="2" stroke="#16a34a"
       style={{ flex: 'none', animation: 'lpIconFlash 2s ease-in-out infinite' }} {...S} aria-hidden="true">
    <path d="M3 12a9 9 0 0 1 9-9 9.75 9.75 0 0 1 6.74 2.74L21 8" /><path d="M21 3v5h-5" />
    <path d="M21 12a9 9 0 0 1-9 9 9.75 9.75 0 0 1-6.74-2.74L3 16" /><path d="M8 16H3v5" />
  </svg>
);
const STAT_ICONS = { doc: IconDoc, book: IconBook, scale: () => <IconScale style={{ flex: 'none' }} />, sync: IconSync };
const IconSparkle = () => (
  <svg width="19" height="19" viewBox="0 0 24 24" fill="#fbbf24" stroke="#fbbf24" strokeWidth="1.5"
       strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"
       style={{ animation: 'lpSparklePulse 2.2s ease-in-out infinite', display: 'inline-block', verticalAlign: -2, marginRight: 8 }}>
    <path d="m12 3-1.912 5.813a2 2 0 0 1-1.275 1.275L3 12l5.813 1.912a2 2 0 0 1 1.275 1.275L12 21l1.912-5.813a2 2 0 0 1 1.275-1.275L21 12l-5.813-1.912a2 2 0 0 1-1.275-1.275L12 3Z" />
  </svg>
);
const IconArrow = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" strokeWidth="2.2" {...S} aria-hidden="true">
    <line x1="5" x2="19" y1="12" y2="12" /><polyline points="12 5 19 12 12 19" />
  </svg>
);
const IconChevron = ({ open }) => (
  <svg width="15" height="15" viewBox="0 0 24 24" strokeWidth="2.4" stroke="#9ca3af" fill="none"
       strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"
       style={{ flex: 'none', display: 'flex', transition: 'transform .2s', transform: `rotate(${open ? 180 : 0}deg)` }}>
    <polyline points="6 9 12 15 18 9" />
  </svg>
);

/** Surlignage vert incliné de la maquette. */
const Highlight = ({ children, small }) => (
  <span style={{ display: 'inline-block', fontWeight: 700, letterSpacing: '-.01em', color: '#15803d',
    background: '#dcfce7', borderRadius: 8, padding: small ? '1px 9px' : '2px 10px', transform: 'rotate(-0.5deg)' }}>
    {children}
  </span>
);

export default function LandingThemePage() {
  const params = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const fileInputRef = useRef(null);
  const [dragging, setDragging] = useState(false);
  const [fileError, setFileError] = useState(null);
  const [openFaq, setOpenFaq] = useState(-1);

  // La route peut être déclarée en littéral (/pv) ou paramétrée : dans
  // le premier cas `params` est vide, on retombe sur le chemin.
  const slug = params.landingSlug || window.location.pathname.split('/').filter(Boolean)[0];
  // Langue partagée : le contenu du thème ET le chrome commun suivent le
  // sélecteur du header. Avant, `LANDING_THEMES[slug]` servait le français
  // quoi qu'il arrive.
  const startRoute = useStartRoute();
  const [language] = useSiteLanguage();
  const t = useLandingT(language);
  const theme = useMemo(() => landingTheme(slug, language), [slug, language]);
  const seoMeta = useMemo(() => getPageMetadata(`landing-${slug}`, language), [slug, language]);

  /* Le parcours d'analyse : c'est la conversion de la page. Visiteur
     connecté → /upload directement ; sinon inscription puis /upload,
     avec le fichier transporté par utils/pendingUpload. */
  const startAnalysis = useCallback((file) => {
    if (file) {
      const err = validatePendingFile(file);
      if (err) { setFileError(err); return; }
      setPendingUpload(file, { theme: slug });
    }
    setFileError(null);
    // Le fichier voyage par `pendingUpload` (variable de module, pas de
    // sessionStorage : un PDF de 20 Mo n'y tiendrait pas). Les DEUX
    // destinations savent le reprendre — /upload depuis toujours,
    // /analyser depuis le dépôt sans inscription.
    if (user) { navigate('/upload'); return; }
    navigate(startRoute === ROUTE_START ? `${ROUTE_START}?next=/upload` : startRoute);
  }, [navigate, user, slug, startRoute]);

  const onDrop = useCallback((e) => {
    e.preventDefault();
    setDragging(false);
    const file = e.dataTransfer?.files?.[0];
    if (file) startAnalysis(file);
  }, [startAnalysis]);

  if (!theme) {
    return (
      <div style={{ padding: 80, textAlign: 'center', color: '#6b7280' }}>
        {t('notFound')} <Link to="/">{t('backHome')}</Link>
      </div>
    );
  }

  const p = theme.proof;
  const canonical = `${SITE_URL}/${slug}`;

  return (
    <div className="landing-theme" lang={language}
         style={{ maxWidth: '100vw', overflowX: 'hidden', background: '#fff', color: INK }}
         data-testid={`landing-${slug}`}>
      <PageHead metadata={seoMeta} />
      <GoogleAnalytics />
      <JsonLd data={createFaqSchema(theme.faqs.map((f) => ({ question: f.q, answer: f.a })))} />
      <JsonLd data={createBreadcrumbSchema([
        { name: t('backHome'), url: SITE_URL },
        { name: theme.faqTitle, url: canonical },
      ])} />

      <PublicHeader />

      {/* ── HERO ──────────────────────────────────────────────────── */}
      {/* HERO — blanc pur : le halo radial bleuté est retiré (rythme commun
          des pages publiques). 140px compensaient une barre fixe ; le
          header public est dans le flux, il reste l'espace visuel. */}
      <section className="lp-hero" style={{ background: '#ffffff', padding: '74px 40px 64px', position: 'relative', overflow: 'hidden' }}>
        <div style={{ maxWidth: 760, margin: '0 auto', textAlign: 'center', position: 'relative' }}>
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 7, background: '#eff6ff', border: '1px solid #dbeafe', color: BLUE, fontSize: 12.5, fontWeight: 600, letterSpacing: '.01em', padding: '6px 14px', borderRadius: 999, marginBottom: 20 }}>
            {theme.badge}
          </span>
          <h1 style={{ fontSize: 'clamp(38px,5.2vw,58px)', fontWeight: 600, letterSpacing: '-.035em', lineHeight: 1.06, margin: 0, color: INK }}>
            {theme.h1[0]}<br /><span style={{ color: BLUE }}>{theme.h1[1]}</span>
          </h1>
          <p style={{ margin: '18px auto 0', maxWidth: 560, fontSize: 16.5, fontWeight: 400, lineHeight: 1.6, color: INK_2 }}>
            {t('heroSubTail')}<Highlight small>{t('heroSubHl')}</Highlight>
          </p>

          <input ref={fileInputRef} type="file" accept=".pdf,.jpg,.jpeg,.png" style={{ display: 'none' }}
                 onChange={(e) => { const f = e.target.files?.[0]; if (f) startAnalysis(f); }}
                 data-testid="landing-file-input" />
          <div
            role="button" tabIndex={0}
            className={`lp-drop${dragging ? ' is-drag' : ''}`}
            onClick={() => fileInputRef.current?.click()}
            onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); fileInputRef.current?.click(); } }}
            onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
            onDragLeave={() => setDragging(false)}
            onDrop={onDrop}
            data-testid="landing-dropzone"
            style={{ maxWidth: 520, margin: '28px auto 0', border: '1.5px dashed #bfdbfe', background: '#f8fbff', borderRadius: 14, padding: '30px 24px', display: 'flex', flexDirection: 'column', alignItems: 'center', cursor: 'pointer', transition: '.15s' }}
          >
            <IconUpload />
            <div style={{ fontSize: 15, fontWeight: 600, letterSpacing: '-.01em', color: INK }}>{theme.dropzone}</div>
            <div style={{ fontSize: 13, fontWeight: 400, color: '#9ca3af', marginTop: 4 }}>{t('dropzoneHint')}</div>
          </div>
          {fileError && (
            <div role="alert" data-testid="landing-file-error"
                 style={{ maxWidth: 520, margin: '10px auto 0', background: '#fef2f2', border: '1px solid #fee2e2', borderRadius: 10, padding: '9px 14px', fontSize: 13, fontWeight: 500, color: '#b91c1c' }}>
              {fileError}
            </div>
          )}
          <div style={{ marginTop: 14, fontSize: 13, fontWeight: 500, color: '#6b7280' }}>{t('heroMicro')}</div>
        </div>
      </section>

      {/* ── CE QU'ARCHER VÉRIFIE ──────────────────────────────────── */}
      <section className="lp-sec" style={{ background: '#f4f4f1', borderTop: '1px solid #e8e8e4', padding: '72px 40px' }}>
        <div style={{ maxWidth: 900, margin: '0 auto' }}>
          <h2 style={{ fontSize: 'clamp(26px,3vw,36px)', fontWeight: 600, letterSpacing: '-.03em', lineHeight: 1.15, margin: '0 0 10px', color: INK }}>
            {theme.checksTitle}
          </h2>
          <p style={{ margin: '0 0 26px', fontSize: 17, fontWeight: 400, lineHeight: 1.65, color: INK_2 }}>
            {theme.checksIntro}, <Highlight>{t('checksHl')}</Highlight>
          </p>
          <div className="lp-checks" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            {theme.checks.map(([bold, rest]) => (
              <div key={bold} style={{ display: 'flex', gap: 11, alignItems: 'flex-start', background: '#fff', border: '1px solid #ececea', borderRadius: 12, padding: '15px 17px', fontSize: 14, fontWeight: 400, lineHeight: 1.5, color: INK_2 }}>
                <span style={{ flex: 'none', width: 20, height: 20, borderRadius: '50%', background: '#dcfce7', display: 'flex', alignItems: 'center', justifyContent: 'center', marginTop: 1 }}>
                  <IconCheck />
                </span>
                <span><span style={{ fontWeight: 600, color: INK }}>{bold}</span>{rest ? ` ${rest}` : ''}</span>
              </div>
            ))}
          </div>
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8, marginTop: 16, fontSize: 13, fontWeight: 400, lineHeight: 1.55, color: '#6b7280' }}>
            <IconScale style={{ flex: 'none', marginTop: 2 }} />
            <span>{theme.legalNote}</span>
          </div>
        </div>
      </section>

      {/* ── BLOC IA + MASCOTTE ────────────────────────────────────── */}
      <section className="lp-sec lp-ai" style={{ background: '#eff6ff', borderTop: '1px solid #dbeafe', borderBottom: '1px solid #dbeafe', padding: '44px 40px' }}>
        <div style={{ maxWidth: 960, margin: '0 auto', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 28, flexWrap: 'wrap' }}>
          {/* Bloc IA, bien en dessous du hero : chargement différé. */}
          <MascotPicture
            width={170} height={150}
            loading="lazy" decoding="async"
            style={{ width: 170, height: 150, flex: 'none', objectFit: 'contain' }}
          />
          <div style={{ flex: '1 1 0', minWidth: 300, maxWidth: 720 }}>
            <div style={{ fontSize: 'clamp(19px,2.2vw,26px)', fontWeight: 600, letterSpacing: '-.025em', lineHeight: 1.3, color: INK }}>
              <IconSparkle />{t('aiLead')}<span style={{ color: BLUE }}>{t('aiLeadAccent')}</span>{t('aiLeadEnd')}{theme.aiTail}
            </div>
            <div className="lp-stats" style={{ display: 'grid', gridTemplateColumns: 'repeat(2,minmax(0,1fr))', gap: 8, marginTop: 20 }}>
              {t('aiStats').map((st, i) => {
                const Ico = STAT_ICONS[AI_STAT_ICONS[i]];
                return (
                  <span key={st.label} style={{ display: 'flex', alignItems: 'center', gap: 10, background: '#fff', border: `1px solid ${AI_STAT_GREEN[i] ? '#dcfce7' : '#dbeafe'}`, borderRadius: 10, padding: '10px 14px', boxShadow: '0 2px 12px rgba(26,86,219,.06)' }}>
                    <Ico />
                    {/* `value: null` → la vignette lit le compte RÉEL du
                        corpus au rendu, comme `engine/KnowledgeBase`. Les
                        autres chiffres de la rangée ne comptent pas des
                        documents et restent tels quels. */}
                    <span style={{ flex: 'none', minWidth: 46, fontSize: 14.5, fontWeight: 700, letterSpacing: '-.01em', color: INK }}>{st.value ?? corpusCountPlus(language)}</span>
                    <span style={{ fontSize: 12.5, fontWeight: 500, color: INK_2, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{st.label}</span>
                  </span>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      {/* ── LA PREUVE ─────────────────────────────────────────────── */}
      <section className="azr-section lp-sec" style={{ padding: '64px 0', background: '#f4f4f1' }}>
        <div className="azr-wrap">
          <div className="azr-proofhead">
            <div className="azr-lab">{t('proofLabel')}</div>
            <div className="azr-h2">{t('proofH2')}<span className="a">{t('proofH2Accent')}</span></div>
            <div className="azr-psub">{t('proofSub')}</div>
          </div>

          <div className="azr-card">
            <span className="azr-ex">{t('example')}</span>
            <div className="azr-head">
              <svg className="azr-ring" viewBox="0 0 126 126" aria-hidden="true">
                <circle cx="63" cy="63" r="52" fill="none" stroke="#F0EEE8" strokeWidth="12" />
                <circle cx="63" cy="63" r="52" fill="none" stroke={p.ringColor} strokeWidth="12"
                        strokeDasharray="327" strokeDashoffset={p.ringOffset} strokeLinecap="round"
                        transform="rotate(-90 63 63)" />
                <text x="63" y="60" textAnchor="middle" fontFamily="Inter" fontSize="30" fontWeight="800" fill={INK}>{p.score}</text>
                <text x="63" y="80" textAnchor="middle" fontFamily="Inter" fontSize="10" fontWeight="700" fill="#9ca3af">/100</text>
              </svg>
              <div>
                <span className={`azr-k ${p.kickerKind}`}>{p.kicker}</span>
                <div className={`azr-h3 ${p.headlineKind}`}>{p.headline}</div>
                <p className="azr-p">{p.summary}</p>
              </div>
            </div>

            <div className="azr-kpis">
              {p.kpis.map((k) => (
                <div className="azr-kpi" key={k.label}>
                  <div className="azr-kl">{k.label}</div>
                  <div className={`azr-kv ${k.kind}`}>{k.value}</div>
                  <div className="azr-ks">{k.sub}</div>
                </div>
              ))}
            </div>

            <div className="azr-body">
              <div className="azr-sect">{t('argsLabel', { n: p.args.length })}</div>
              {p.args.map((a, i) => (
                <div className={`azr-arg${a.weak ? ' m' : ''}`} key={a.title}>
                  <span className="azr-num">{String(i + 1).padStart(2, '0')}</span>
                  <div>
                    <div className="azr-at">{a.title}</div>
                    <div className="azr-ab">{t('legalBasis')} <b>{a.basis}</b></div>
                  </div>
                  <span className={`azr-tag ${a.weak ? 'm' : 'f'}`}>{a.tag}</span>
                </div>
              ))}

              <div className="azr-split">
                <div>
                  <div className="azr-sect">{t('letterLabel')}</div>
                  <div className="azr-let">
                    {p.letter.map((l) => (
                      <div key={l}><span className="azr-c">✓</span><span>{l}</span></div>
                    ))}
                  </div>
                  <button type="button" className="azr-cta" onClick={() => startAnalysis(null)}
                          data-testid="landing-proof-cta">
                    {p.cta}
                  </button>
                </div>
                <div>
                  <div className="azr-sect">{t('projectionLabel')}</div>
                  <div className="azr-pbar">
                    {p.projection.map((seg) => (
                      <div key={seg.label} style={{ width: seg.width, background: seg.color }} />
                    ))}
                  </div>
                  <div className="azr-pl">
                    {p.projection.map((seg) => (
                      <span key={seg.label}>{seg.label} <b>{seg.value}</b></span>
                    ))}
                  </div>
                  <div className="azr-note">{p.note}</div>
                </div>
              </div>
            </div>

            <div className="azr-foot">
              {p.foot.map((f) => <span key={f}>{f}</span>)}
            </div>
          </div>

          <div className="azr-cap">{t('proofCap')}</div>

          {/* ── LES 3 CARTES D'ACTION ─────────────────────────────── */}
          <div className="azr-acts-h">
            <div className="t">{t('actsTitle')}</div>
            <div className="s">{t('actsSub')}</div>
          </div>
          <div className="azr-acts">
            <div className="azr-act low">
              <div className="azr-aicon">✍️</div>
              <div className="azr-at2">{t('actSelfTitle')}</div>
              <div className="azr-ap">{t('actSelfBody')}</div>
              <div className="azr-aprice">{t('actSelfPrice')}</div>
              <button type="button" className="azr-abtn" onClick={() => startAnalysis(null)}
                      data-testid="landing-act-self">
                {t('actSelfCta')}
              </button>
            </div>
            <div className="azr-act hero">
              <span className="azr-badge">{t('actJuristBadge')}</span>
              <div className="azr-aicon">📭</div>
              <div className="azr-at2">{t('actJuristTitle')}</div>
              <div className="azr-ap">{t('actJuristBody')}</div>
              <div className="azr-aprice">{p.juristPrice}</div>
              <button type="button" className="azr-abtn" onClick={() => navigate(ROUTE_PLANS)}
                      data-testid="landing-act-jurist">
                {t('actJuristCta')}
              </button>
            </div>
            <div className="azr-act mid">
              <div className="azr-aicon">⚖️</div>
              <div className="azr-at2">{t('actLawyerTitle')}</div>
              <div className="azr-ap">{t('actLawyerBody')}</div>
              <div className="azr-aprice">{t('actLawyerPrice')}</div>
              <button type="button" className="azr-abtn" onClick={() => navigate(ROUTE_ATTORNEYS)}
                      data-testid="landing-act-attorney">
                {t('actLawyerCta')}
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* ── BANDE JURISTE / AVOCAT ────────────────────────────────── */}
      <section className="lp-sec" style={{ background: '#fff', padding: '30px 40px', borderBottom: '1px solid #f4f4f1' }}>
        <div style={{ maxWidth: 900, margin: '0 auto', textAlign: 'center', fontSize: 15, fontWeight: 400, lineHeight: 1.65, color: '#374151' }}>
          <span style={{ fontWeight: 600, color: INK }}>{theme.bandLead}</span>{' '}
          <span style={{ fontWeight: 600, color: INK }}>{t('bandTail1')}</span>{t('bandTail2')}
          <span style={{ fontWeight: 600, color: INK }}>{t('bandTail3')}</span>{t('bandTail4')}
          <Highlight>{t('bandHl')}</Highlight>
        </div>
      </section>

      {/* ── FAQ ───────────────────────────────────────────────────── */}
      <section className="lp-sec" style={{ background: '#f4f4f1', padding: '64px 40px' }}>
        <div style={{ maxWidth: 760, margin: '0 auto' }}>
          <h2 style={{ fontSize: 'clamp(24px,2.6vw,32px)', fontWeight: 600, letterSpacing: '-.028em', margin: '0 0 22px', color: INK }}>
            {theme.faqTitle}
          </h2>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {theme.faqs.map((f, i) => {
              const open = openFaq === i;
              return (
                <div key={f.q} className="lp-faq"
                     style={{ background: '#fff', border: `1px solid ${open ? '#dbeafe' : '#ececea'}`, borderRadius: 12, padding: '16px 20px', transition: 'all .18s ease' }}>
                  <button type="button" aria-expanded={open} aria-controls={`lp-faq-${i}`}
                          onClick={() => setOpenFaq((c) => (c === i ? -1 : i))}
                          data-testid={`landing-faq-${i}`}
                          style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 14, width: '100%', padding: 0, background: 'none', border: 'none', cursor: 'pointer', textAlign: 'left', fontFamily: 'inherit' }}>
                    <span style={{ fontSize: 15, fontWeight: 600, letterSpacing: '-.012em', color: INK }}>{f.q}</span>
                    <IconChevron open={open} />
                  </button>
                  {open && (
                    <div id={`lp-faq-${i}`} style={{ fontSize: 14, fontWeight: 400, lineHeight: 1.65, color: INK_2, marginTop: 10 }}>
                      {f.a}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ── CTA FINAL ─────────────────────────────────────────────── */}
      <section className="lp-sec" style={{ background: '#fff', borderTop: '1px solid #f4f4f1', padding: '64px 40px 80px', textAlign: 'center' }}>
        <div style={{ maxWidth: 640, margin: '0 auto' }}>
          <h2 style={{ fontSize: 'clamp(28px,3.4vw,40px)', fontWeight: 600, letterSpacing: '-.032em', lineHeight: 1.12, margin: 0, color: INK }}>
            {theme.finalCta[0]}<br /><span style={{ color: BLUE }}>{theme.finalCta[1]}</span>
          </h2>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 20, marginTop: 26, flexWrap: 'wrap' }}>
            <button type="button" className="lp-cta" onClick={() => startAnalysis(null)}
                    data-testid="landing-final-cta"
                    style={{ display: 'inline-flex', alignItems: 'center', gap: 9, padding: '14px 26px', border: 'none', background: BLUE, color: '#fff', borderRadius: 11, fontSize: 15.5, fontWeight: 600, letterSpacing: '-.01em', fontFamily: 'inherit', cursor: 'pointer', transition: '.15s', boxShadow: '0 4px 16px rgba(26,86,219,.22)' }}>
              {theme.finalCtaBtn}<IconArrow />
            </button>
          </div>
          <div style={{ marginTop: 14, fontSize: 13, fontWeight: 400, color: '#9ca3af' }}>{t('finalSub')}</div>
        </div>
      </section>

      <SiteFooterV2 />
    </div>
  );
}
